import {Box, Divider, IconButton, IconButtonProps, ListItemIcon, Menu, MenuItem, Typography} from '@mui/material';
import {useState} from 'react';
import {useTolgee, useTranslation} from 'tolgee';

import {INTERCOM_USER_GUIDE_ID_DE, INTERCOM_USER_GUIDE_ID_EN} from '~constants/constants';
import {theme} from '~constants/theme';
import {useTrackEvent} from '~hooks/analytics.hooks';
import {getUtilStateIsLoading} from '~redux/reducers/globalReducer';
import {useAppSelector} from '~redux/index';

import {useUserback} from '~components/common/UserbackProvider';
import {HelpIcon, SmallBookOpenIcon, SmallChatIcon, SmallSchoolOutlineIcon} from 'src/icons';

const KNOWLEDGE_BASE_URL = 'https://support.maddox.ai/knowledge-center';

function getIntercomUserGuideID(language: string) {
  if (language === 'en') {
    return INTERCOM_USER_GUIDE_ID_EN;
  } else if (language === 'de') {
    return INTERCOM_USER_GUIDE_ID_DE;
  }
  return INTERCOM_USER_GUIDE_ID_EN;
}

export function HelpButton({
  sx,
  menuZIndex,
  idPrefix = 'help',
  showOnboardingGuide = true,
  ...other
}: IconButtonProps & {menuZIndex?: number; idPrefix?: string; showOnboardingGuide?: boolean}) {
  const {t} = useTranslation('common');
  const isLoading = useAppSelector(getUtilStateIsLoading);
  const trackEvent = useTrackEvent();
  const userbackInstance = useUserback();
  const tolgee = useTolgee();
  const language = tolgee.getLanguage()?.substring(0, 2) as 'en' | 'de';

  const [helpMenuAnchor, setHelpMenuAnchor] = useState<HTMLElement | null>(null);
  const showHelpMenu = Boolean(helpMenuAnchor);

  function showMenu(event: React.MouseEvent<HTMLElement>) {
    setHelpMenuAnchor(event.currentTarget);
  }

  function closeMenu() {
    setHelpMenuAnchor(null);
  }

  function openIntercomMessenger(event?: React.MouseEvent<HTMLButtonElement>) {
    event?.stopPropagation();
    if (typeof window !== 'undefined') {
      const intercom = (window as unknown as {Intercom?: (...args: any[]) => void}).Intercom;
      intercom?.('startChecklist', getIntercomUserGuideID(language));
    }
    trackEvent('help_intercom_onboarding_start');
    closeMenu();
  }

  function openFeedbackForm() {
    if (userbackInstance) {
      userbackInstance.openForm();
    } else {
      console.warn('Userback is not ready yet');
    }
  }

  return (
    <>
      <Box sx={{isolation: 'isolate', display: 'inline-block', zIndex: isLoading ? 'auto' : 9999}}>
        <IconButton
          id={`${idPrefix}-button`}
          onClick={showMenu}
          title={t('helpAndFeedback')}
          aria-label={t('helpAndFeedback')}
          size='small'
          sx={[
            (theme) => ({
              backgroundColor: theme.palette.background.default,
              mixBlendMode: 'normal',
              position: 'relative',
              marginTop: '-4px',
              borderRadius: theme.spacing(14),
              border: `1px solid ${theme.palette.grey[400]}`,
              gap: theme.spacing(0.5),
              height: theme.spacing(5),
              paddingX: theme.spacing(1.5),
              textTransform: 'none',
              cursor: 'pointer',
              '&:hover': {backgroundColor: theme.palette.grey[200]},
            }),
            ...(Array.isArray(sx) ? sx : [sx]),
          ]}
          data-testid={`${idPrefix}-button`}
          {...other}
        >
          <HelpIcon
            sx={{
              color: theme.palette.secondary.dark,
              width: 20,
              height: 20,
            }}
          />
          <Typography variant='body2' color='secondary.dark'>
            {t('helpAndFeedback')}
          </Typography>
        </IconButton>
      </Box>

      <Menu
        id={`${idPrefix}-menu`}
        open={showHelpMenu}
        anchorEl={helpMenuAnchor}
        sx={menuZIndex ? {zIndex: menuZIndex} : undefined}
        onClick={closeMenu}
        onClose={closeMenu}
        transformOrigin={{horizontal: 'right', vertical: 'top'}}
        anchorOrigin={{horizontal: 'right', vertical: 'bottom'}}
        PaperProps={{
          elevation: 0,
          sx: {
            overflow: 'visible',
            filter: 'drop-shadow(0px 2px 8px rgba(0,0,0,0.32))',
            marginTop: 1,
            '&:before': {
              // creates an arrow pointing upwards
              content: '""',
              display: 'block',
              position: 'absolute',
              top: 0,
              right: 20,
              width: 10,
              height: 10,
              bgcolor: 'background.paper',
              transform: 'translateY(-50%) rotate(45deg)',
            },
          },
        }}
      >
        {showOnboardingGuide && (
          <MenuItem
            onClick={() => openIntercomMessenger()}
            id={`${idPrefix}-menu-getting-started`}
            data-testid={`${idPrefix}-menu-getting-started`}
          >
            <Box display='flex' alignItems='center' gap={theme.spacing(1)}>
              <ListItemIcon
                sx={{
                  backgroundColor: '#C1C1FF4D',
                  padding: theme.spacing(1),
                  borderRadius: theme.spacing(0.5),
                  color: theme.palette.secondary.light,
                }}
              >
                <SmallSchoolOutlineIcon />
              </ListItemIcon>
              <Box>
                <Typography variant='body1' fontWeight={600} color={theme.palette.maddox.globalblack}>
                  {t('gettingStartedGuide.title')}
                </Typography>
                <Typography variant='body2' color={theme.palette.maddox.black80}>
                  {t('gettingStartedGuide.description')}
                </Typography>
              </Box>
            </Box>
          </MenuItem>
        )}
        <MenuItem
          onClick={() => window.open(KNOWLEDGE_BASE_URL, '_blank')}
          data-testid={`${idPrefix}-menu-knowledge-center`}
        >
          <Box display='flex' alignItems='center' gap={theme.spacing(1)}>
            <ListItemIcon
              sx={{
                backgroundColor: theme.palette.maddox.black10,
                padding: theme.spacing(1),
                borderRadius: theme.spacing(0.5),
                color: theme.palette.maddox.black60,
              }}
            >
              <SmallBookOpenIcon />
            </ListItemIcon>
            <Box>
              <Typography variant='body1' fontWeight={600} color={theme.palette.maddox.globalblack}>
                {t('knowledgeCenter.title')}
              </Typography>
              <Typography variant='body2' color={theme.palette.maddox.black80}>
                {t('knowledgeCenter.description')}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
        <Divider
          sx={{
            '&.MuiDivider-root': {
              margin: 0,
            },
          }}
        />
        <MenuItem
          onClick={openFeedbackForm}
          id={`${idPrefix}-menu-give-feedback`}
          data-testid={`${idPrefix}-menu-give-feedback`}
        >
          <Box display='flex' alignItems='center' gap={theme.spacing(1)}>
            <ListItemIcon
              sx={{
                backgroundColor: 'rgba(235, 47, 245, 0.2)',
                padding: theme.spacing(1),
                borderRadius: theme.spacing(0.5),
                color: '#DB00A5',
              }}
            >
              <SmallChatIcon />
            </ListItemIcon>
            <Box height='46px' display='flex' alignItems='center'>
              <Typography variant='body1' fontWeight={600} color={theme.palette.maddox.globalblack}>
                {t('giveFeedback')}
              </Typography>
            </Box>
          </Box>
        </MenuItem>
      </Menu>
    </>
  );
}
